/**
 * Drops Sentry's automatic `console` breadcrumbs for log lines that carry data
 * we deliberately keep out of Sentry.
 *
 * Sentry's default `consoleIntegration()` copies console arguments into
 * `breadcrumb.data.arguments` verbatim, and neither `stripPiiFromEvent` nor
 * `scrubInviteCodeFromEvent` touches that key.
 *
 * Example: the invite-code throttle logs the normalised client IP — permitted
 * in our own logs but not in Sentry ("no emails, names, or IPs") — immediately
 * before a `captureMessage`, so the breadcrumb would otherwise ride along.
 *
 * Dependency-free (no `@sentry/nextjs` import) so it is usable from the server,
 * edge and client Sentry configs. `consoleSandbox()` is not an option:
 * `@sentry/nextjs` does not re-export it and `@sentry/core` is not a direct
 * dependency.
 */

import { SENSITIVE_CONSOLE_LOG_PREFIXES } from '@/lib/security/sensitive-log-prefixes';

export { SENSITIVE_CONSOLE_LOG_PREFIXES };

/** The minimal breadcrumb shape this module inspects. */
export interface ConsoleBreadcrumbLike {
  category?: string;
  message?: string;
  data?: { arguments?: unknown[] } & Record<string, unknown>;
}

/**
 * `beforeBreadcrumb` handler: returns `null` (drop) for a console breadcrumb
 * produced by one of {@link SENSITIVE_CONSOLE_LOG_PREFIXES}, and the breadcrumb
 * unchanged otherwise.
 *
 * Only `category === 'console'` breadcrumbs are considered — navigation, fetch,
 * XHR and UI breadcrumbs are left alone, and URL redaction for those is
 * `scrubInviteCodeFromEvent`'s job, not this one.
 */
export function dropSensitiveConsoleBreadcrumb<T extends ConsoleBreadcrumbLike>(
  breadcrumb: T
): T | null {
  if (breadcrumb.category !== 'console') {
    return breadcrumb;
  }

  const firstArgument = breadcrumb.data?.arguments?.[0];
  const candidates = [
    breadcrumb.message,
    typeof firstArgument === 'string' ? firstArgument : undefined
  ];

  for (const candidate of candidates) {
    if (
      typeof candidate === 'string' &&
      SENSITIVE_CONSOLE_LOG_PREFIXES.some((prefix) =>
        candidate.startsWith(prefix)
      )
    ) {
      return null;
    }
  }

  return breadcrumb;
}
